/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderUserContext, isJobEditable } from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  hasFormStateError,
  isRouteForCreationMode,
  markFormStateAsTouched,
  MutablePageState,
  setEntityFields,
  setFormStateSnapshotValue,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { find, first, isNil, last, map, omit, pull, without } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { jobSpecQuoteChangeFromSection, validateJobSpecSectionInfo } from '../../';
import {
  getJobSpecQuoteChangeId,
  isNotQcParagraph,
  JobSpecDetails,
  JobSpecInfo,
  JobSpecParagraphDetails,
  JobSpecParagraphInfo,
  JobSpecQuoteChange,
  jobSpecQuoteChangeArr,
  jobSpecQuoteChangeToSection,
  JobSpecSectionDetails,
  JobSpecSectionInfo,
  JobSpecSelection,
  JobSpecSelectionLevel,
  jobSpecTreeHelper,
  validateJobSpecParagraphInfo,
} from '../../resources/job.model';

@Injectable()
export class JobSpecDetailsFormState extends MutablePageState<JobSpecDetails> {
  private selection$$ = new BehaviorSubject<JobSpecSelection>({});

  constructor(private route: ActivatedRoute,
              private userContext: BuilderUserContext) {
    super();
  }

  get selection(): JobSpecSelection {
    return this.selection$$.value;
  }

  get selectionChanges(): Observable<JobSpecSelection> {
    return this.selection$$.asObservable();
  }

  get quoteChanges(): JobSpecQuoteChange[] {
    const quoteChangesParagraph = last(this.value.paragraphs);

    return quoteChangesParagraph && quoteChangesParagraph.quoteChanges || [];
  }

  get invalid(): boolean {
    // check validity of top form
    const { masterInfo } = this.value;
    if (hasFormStateError(masterInfo)) {
      return true;
    }

    // check validity of tree content
    return jobSpecTreeHelper.reduceNodes(
      this.value.paragraphs,
      (invalid: boolean, node, _1, _2, path) =>
        invalid || this.isNodeInvalid(path),
      false);
  }

  get isContractSigned(): boolean {
    return this.value.isContractSigned;
  }

  build(job: JobSpecDetails): void {
    this.setMode(isRouteForCreationMode(this.route)
      ? FormMode.CreateMode
      : (this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT)
      && isJobEditable(getFormStateSnapshotValue(job.masterInfo))
        ? FormMode.EditMode : FormMode.ViewMode));
    this.setValue(job);

    this.selectFirstParagraph();
  }

  merge(details: JobSpecDetails): void {
    jobSpecTreeHelper.merge(this.value.paragraphs, details.paragraphs, (thisNode, anotherNode, level) => {
      if (level === JobSpecSelectionLevel.Section) {
        thisNode.isDraggable = false;
        thisNode.info = setFormStateSnapshotValue<JobSpecSectionInfo>(
          thisNode.info,
          (info) =>
            setEntityFields(info, {
              clause: void 0,
              'clauses': map(info.clauses, (clause) => omit(clause, 'quoteChange')),
            }));
      }
    });

    this.value.version = details.version;
    this.emitChange();
  }

  highlightAll(): void {
    // highlight top form
    const { masterInfo } = this.value;
    if (hasFormStateError(masterInfo)) {
      this.value.masterInfo = markFormStateAsTouched(masterInfo);
      return;
    }

    // find the first invalid tree node
    const invalidPath = jobSpecTreeHelper.reduceNodes(
      this.value.paragraphs,
      (invalid, node, _1, _2, path) => {
        if (invalid) {
          return invalid;
        }

        return this.isNodeInvalid(path) ? path : void 0;
      },
      <Maybe<any[]>>void 0);

    if (invalidPath) {
      const lastNode = last(invalidPath)!; // tslint:disable-line:no-non-null-assertion
      lastNode.info = markFormStateAsTouched(lastNode.info);
      this.selectByPath(invalidPath);
    }
  }

  selectByPath(path: any[]): void {
    switch (path.length) {
      case JobSpecSelectionLevel.Paragraph:
        this.selectParagraph(path[0]);
        break;
      case JobSpecSelectionLevel.Section:
        this.selectSection(path[0], path[1]);
        break;
      default:
        this.resetSelection();
        break;
    }
  }

  selectParagraph(paragraph: JobSpecParagraphDetails): void {
    this.selection$$.next({
      level: JobSpecSelectionLevel.Paragraph,
      paragraph,
      section: void 0,
    });
  }

  selectSection(paragraph: JobSpecParagraphDetails, section: JobSpecSectionDetails): void {
    this.selection$$.next({
      level: JobSpecSelectionLevel.Section,
      paragraph,
      section,
    });
  }

  resetSelection(): void {
    this.selection$$.next({
      level: void 0,
      paragraph: void 0,
      section: void 0,
    });
  }

  moveNodes(): void {
    this.setDirty();
    this.emitChange();
  }

  updateMasterInfo(masterInfo: FormStateSnapshot<JobSpecInfo>): void {
    this.value.masterInfo = masterInfo;
    this.setDirty();
    this.emitChange();
  }

  updateParagraphData(paragraph: JobSpecParagraphDetails,
                      paragraphData: FormStateSnapshot<JobSpecParagraphInfo>): void {
    paragraph.info = paragraphData;
    this.setDirty();
    this.emitChange();
  }

  updateSectionData(section: JobSpecSectionDetails,
                    sectionData: FormStateSnapshot<JobSpecSectionInfo>): void {
    section.info = sectionData;
    this.setDirty();
    this.emitChange();
  }

  updateQuoteChanges(quoteChanges: JobSpecQuoteChange[]): void {
    const quoteChangesParagraph = last(this.value.paragraphs);
    if (quoteChangesParagraph) {
      quoteChangesParagraph.quoteChanges = quoteChanges;
    }
    this.setDirty();
    this.emitChange();
  }

  moveQuoteChangeIntoParagraph(paragraph: JobSpecParagraphDetails, index: number, qc: JobSpecQuoteChange): void {
    // add quote change into paragraph as section
    paragraph.children.splice(index, 0, jobSpecQuoteChangeToSection(qc));
    // remove moved quote change from set of quote changes
    this.updateQuoteChanges(jobSpecQuoteChangeArr.remove(this.quoteChanges, getJobSpecQuoteChangeId(qc)));
  }

  moveQuoteChangeIntoSection(section: JobSpecSectionDetails, quoteChange: JobSpecQuoteChange): void {
    const clause = {
      text: quoteChange.text,
      quoteChange,
      isTextWarning: false,
    };
    section.info = setFormStateSnapshotValue(
      section.info,
      (info) =>
        setEntityFields(
          info,
          {
            clauseValue: clause.text,
            clauses: info.clauses.concat(clause),
            clause,
          }));
    // remove moved quote change from set of quote changes
    this.updateQuoteChanges(jobSpecQuoteChangeArr.remove(this.quoteChanges, getJobSpecQuoteChangeId(quoteChange)));
  }

  moveQuoteChangeFromParagraphToSection(sourceParagraph: JobSpecParagraphDetails,
                                        sourceSection: JobSpecSectionDetails,
                                        targetSection: JobSpecSectionDetails): void {
    this.backSectionQuoteChange(sourceParagraph, sourceSection);
    const quoteChange = this.getQuoteChange(sourceSection.id);
    if (quoteChange) {
      this.moveQuoteChangeIntoSection(targetSection, quoteChange);
    }
  }

  backSectionQuoteChange(paragraph: JobSpecParagraphDetails, section: JobSpecSectionDetails): void {
    this.ensureExistingNodeSelected([paragraph, section]);

    // remove quote change from paragraph
    pull(paragraph.children, section);
    // add section as quote change
    this.updateQuoteChanges(jobSpecQuoteChangeArr.insertSorted(
      this.quoteChanges,
      jobSpecQuoteChangeFromSection(section),
      'name'));
  }

  backClauseQuoteChange(section: JobSpecSectionDetails, quoteChangeToRemove: JobSpecQuoteChange): void {
    section.info = setFormStateSnapshotValue(section.info, (info) => {
      const quoteChangeClause = find(info.clauses, ({ quoteChange }) => quoteChange === quoteChangeToRemove);
      if (isNil(quoteChangeClause)) {
        return info;
      }

      return setEntityFields(info, {
        clauses: without(info.clauses, quoteChangeClause),
        clause: info.clause === quoteChangeClause ? void 0 : quoteChangeClause,
        clauseValue: info.clause === quoteChangeClause ? void 0 : quoteChangeClause,
      });
    });

    // add section as quote change
    this.updateQuoteChanges(jobSpecQuoteChangeArr.insertSorted(
      this.quoteChanges,
      quoteChangeToRemove,
      'name'));
  }

  private selectFirstParagraph(): void {
    const firstParagraph = first(this.value.paragraphs);
    if (firstParagraph) {
      this.selectParagraph(firstParagraph);
    } else {
      this.selection$$.next({});
    }
  }

  private isNodeInvalid(path: any[]): boolean {
    const level = path.length;
    const node = last(path);
    const parent = path[path.length - 2]; // tslint:disable-line:no-magic-numbers
    switch (level) {
      case JobSpecSelectionLevel.Paragraph: {
        const data: JobSpecParagraphInfo = getFormStateSnapshotValue(node.info);
        return isNotQcParagraph(node.info) ? isNotNil(validateJobSpecParagraphInfo(data)) : false;
      }
      case JobSpecSelectionLevel.Section: {
        const data: JobSpecSectionInfo = getFormStateSnapshotValue(node.info);
        const parentData: JobSpecParagraphInfo = getFormStateSnapshotValue(parent.info);
        return parentData.isParagraphIncluded && isNotNil(validateJobSpecSectionInfo(data));
      }
      default:
        return false;
    }
  }

  private getQuoteChange(id: ID): Maybe<JobSpecQuoteChange> {
    return jobSpecQuoteChangeArr.find(this.quoteChanges, id);
  }

  private ensureExistingNodeSelected(removedPath: any[]): void {
    const { selection } = this;
    const path = jobSpecTreeHelper.findNextNode(
      this.value.paragraphs,
      removedPath,
      jobSpecTreeHelper.createPath(selection.paragraph, selection.section));

    this.selectByPath(path);
  }
}
